import { useEffect, useState } from 'react';
import { Compass, PlayCircle, Sparkles, Map, LayoutDashboard, Wrench, ListChecks, MessageSquare, Plug } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { startFirstProjectTour } from '@/components/FirstProjectTour';
import { resetTour } from '@/components/UITour';
import { dispatchUiTourStart } from '@/utils/uiTourEvents';

/**
 * TourCenter — the single "one entry point" for every guided walkthrough
 * in the app. Replaces the ad-hoc collection of per-panel help buttons as
 * the *discoverable* home; the small `?` buttons still work inline for
 * local reach.
 *
 * Each entry either:
 *  - dispatches a `tour:open:<tourId>` event picked up by the matching
 *    `TooltipMiniTour` mounted somewhere in the app, or
 *  - triggers a legacy tour launcher (First Project, Full UI Tour).
 */

interface TourEntry {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  launch: (navigate: ReturnType<typeof useNavigate>) => void;
  route?: string;
}

const TOURS: TourEntry[] = [
  {
    id: 'first-project',
    title: 'First Project walkthrough',
    description: 'The best starting point — an end-to-end tour of the sample project (chat, toolbar, Gantt).',
    icon: Sparkles,
    launch: (navigate) => { navigate('/app'); setTimeout(() => startFirstProjectTour(), 300); },
  },
  {
    id: 'chat-sidebar',
    title: 'AI Control Panel',
    description: 'How the chat modes, forms, command input, and voice input work.',
    icon: MessageSquare,
    launch: (navigate) => { navigate('/app'); setTimeout(() => window.dispatchEvent(new Event('tour:open:chat-sidebar')), 300); },
  },
  {
    id: 'gantt-toolbar',
    title: 'Gantt toolbar',
    description: 'Workspace picker, View By, Undo/Redo, and project actions.',
    icon: Wrench,
    launch: (navigate) => { navigate('/app'); setTimeout(() => window.dispatchEvent(new Event('tour:open:gantt-toolbar')), 300); },
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Overall progress and view levels (High Level, Focused, Team).',
    icon: LayoutDashboard,
    launch: (navigate) => { navigate('/app'); setTimeout(() => window.dispatchEvent(new Event('tour:open:dashboard')), 300); },
  },
  {
    id: 'integrations',
    title: 'Integrations Hub',
    description: 'Connected, Browse, and Advanced tabs — plus the chat commands.',
    icon: Plug,
    launch: (navigate) => { navigate('/app/integrations'); setTimeout(() => window.dispatchEvent(new Event('tour:open:integrations-hub')), 500); },
  },
  {
    id: 'gantt-chart',
    title: 'Gantt chart basics',
    description: 'Task list, timeline, mini-map, and resource histogram.',
    icon: ListChecks,
    launch: (navigate) => { navigate('/app'); setTimeout(() => window.dispatchEvent(new Event('tour:open:gantt-chart')), 300); },
  },
  {
    id: 'ui-tour',
    title: 'Full UI tour',
    description: 'A slower walkthrough of the whole app shell — sidebar, help, chat, toolbar, Gantt.',
    icon: Map,
    launch: (navigate) => { navigate('/app'); resetTour(); setTimeout(() => dispatchUiTourStart(), 300); },
  },
];

interface TourCenterProps {
  trigger?: React.ReactNode;
}

export function TourCenter({ trigger }: TourCenterProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('tour-center:open', handler);
    return () => window.removeEventListener('tour-center:open', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="gap-1.5 text-xs" title="Tour Center">
            <Compass className="h-3.5 w-3.5" />
            Tours
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Compass className="h-5 w-5 text-primary" />
            Tour Center
          </DialogTitle>
          <DialogDescription>
            One place to (re-)start every guided walkthrough. Small ? buttons still open the same tours inline.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border rounded-md border border-border">
          {TOURS.map((t) => {
            const Icon = t.icon;
            return (
              <li key={t.id} className="flex items-start gap-3 p-3">
                <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{t.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs shrink-0"
                  onClick={() => { setOpen(false); t.launch(navigate); }}
                >
                  <PlayCircle className="h-3.5 w-3.5" />
                  Start
                </Button>
              </li>
            );
          })}
        </ul>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-1">
          <ListChecks className="h-3 w-3" />
          Chat command: <code className="px-1 py-0.5 rounded bg-muted">open tour center</code>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Fire from anywhere to open the Tour Center. */
export function openTourCenter() {
  window.dispatchEvent(new Event('tour-center:open'));
}
